import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { formatarDinheiro } from '@/pages/gerenciar/utils';
import type { PontoPainel } from '@/pages/fichas/types';

interface Props {
  eventoId: number;
  pontos: PontoPainel[];
  rotuloPonto: string;
  rotuloPontos: string;
  aoNova: () => void;
  aoEditar: (p: PontoPainel) => void;
  aoConvidar: (p: PontoPainel) => void;
  aoSalvo: () => void;
}

// Lista de pontos de venda: cada um é um cartão, com interruptor aberta/pausada
// e atalhos. "Novo ponto" fica como botão tracejado no fim.
export default function PainelBarracas({
  pontos,
  rotuloPonto,
  rotuloPontos,
  aoNova,
  aoEditar,
  aoConvidar,
  aoSalvo,
}: Props) {
  const [trocandoId, setTrocandoId] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  async function alternar(p: PontoPainel) {
    setTrocandoId(p.ponto_id);
    setErro(null);
    try {
      const { error } = await supabase.rpc('loja_pausar_ponto', {
        p_ponto: p.ponto_id,
        p_pausar: p.aberto,
      });
      if (error) throw error;
      aoSalvo();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setTrocandoId(null);
    }
  }

  return (
    <div className="rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <div className="flex items-center justify-between gap-3 px-1">
        <h2 className="text-[17px] font-bold text-foreground-950">{rotuloPontos}</h2>
      </div>

      {erro && <p className="mt-2 px-1 text-[14px] text-danger-600">{erro}</p>}

      {pontos.length === 0 ? (
        <p className="px-1 pt-2 text-[14px] text-foreground-500">
          Ainda não há {rotuloPonto.toLowerCase()}. Crie o primeiro para começar a montar as fichas.
        </p>
      ) : (
        <ul className="mt-2">
          {pontos.map((p) => {
            const trocando = trocandoId === p.ponto_id;
            return (
              <li key={p.ponto_id} className="border-b border-background-100 py-2 last:border-0">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => aoEditar(p)}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                      <i className="ri-store-2-line text-lg" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold text-foreground-950">
                        {p.nome}
                      </span>
                      <span className="mt-0.5 block truncate text-[13px] text-foreground-500">
                        {p.vendidos} vendidos · {formatarDinheiro(p.receita_centavos)}
                        {p.acabando > 0 ? ` · ${p.acabando} acabando` : ''}
                      </span>
                    </span>
                  </button>

                  <Link
                    to={`/loja/barraca?ponto=${p.ponto_id}`}
                    className="shrink-0 whitespace-nowrap text-[13px] font-semibold text-primary-700"
                  >
                    Abrir {rotuloPonto.toLowerCase()} ›
                  </Link>

                  <button
                    type="button"
                    onClick={() => aoConvidar(p)}
                    aria-label={`Convidar operador para ${p.nome}`}
                    className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl text-foreground-400 transition-colors hover:bg-primary-50 hover:text-primary-600"
                  >
                    <i className="ri-user-add-line text-lg" />
                  </button>

                  <button
                    type="button"
                    role="switch"
                    aria-checked={p.aberto}
                    aria-label={p.aberto ? `Pausar ${p.nome}` : `Reabrir ${p.nome}`}
                    onClick={() => alternar(p)}
                    disabled={trocando}
                    className={`relative h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                      p.aberto ? 'bg-success-500' : 'bg-secondary-300'
                    }`}
                  >
                    <span
                      className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-all ${
                        p.aberto ? 'left-6' : 'left-1'
                      }`}
                    />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <button
        type="button"
        onClick={aoNova}
        className="mt-2 flex min-h-[48px] w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl border border-dashed border-primary-300 px-4 text-[14px] font-semibold text-primary-700 transition-colors hover:bg-primary-50"
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-add-line" />
        </span>
        Adicionar {rotuloPonto.toLowerCase()}
      </button>
    </div>
  );
}